import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * 30-minute "sample" window. Every visitor (anonymous or logged-in) gets
 * 30 minutes of unrestricted Hub access before any upgrade nudge fires.
 *
 * - Anonymous users: timer lives in localStorage only.
 * - Logged-in users: also mirrored to `profiles.sample_started_at` so
 *   clearing localStorage / switching device doesn't reset it.
 *
 * Consumers should read `suppressUpsells` and short-circuit any paywall /
 * upgrade modals while the sample is active.
 */
const SAMPLE_MS = 30 * 60 * 1000;
const LS_KEY = "hl_sample_started_at";

type Ctx = {
  startedAt: number | null;
  remainingMs: number;
  expired: boolean;
  suppressUpsells: boolean;
  ready: boolean;
};

const SampleCtx = createContext<Ctx>({
  startedAt: null,
  remainingMs: SAMPLE_MS,
  expired: false,
  suppressUpsells: true,
  ready: false,
});

function readLocal(): number | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function writeLocal(ts: number) {
  try {
    localStorage.setItem(LS_KEY, String(ts));
  } catch {
    /* ignore quota */
  }
}

export function SampleWindowProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [startedAt, setStartedAt] = useState<number | null>(() => readLocal());
  const [now, setNow] = useState(() => Date.now());
  const [ready, setReady] = useState(false);

  // 1s tick — light, only one timer for the whole app.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Bootstrap timer for anonymous users immediately; for logged-in users,
  // reconcile with profiles.sample_started_at (server wins if older).
  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;

    (async () => {
      const local = readLocal();

      if (!user) {
        // Anonymous: ensure a timer exists.
        if (local == null) {
          const ts = Date.now();
          writeLocal(ts);
          if (!cancelled) setStartedAt(ts);
        } else if (!cancelled) {
          setStartedAt(local);
        }
        if (!cancelled) setReady(true);
        return;
      }

      // Logged-in: pull from profile, fall back to local, else start now.
      try {
        const { data } = await supabase
          .from("profiles")
          .select("sample_started_at")
          .eq("user_id", user.id)
          .maybeSingle();
        const serverIso = (data as any)?.sample_started_at as string | null | undefined;
        const server = serverIso ? new Date(serverIso).getTime() : null;

        // Server wins if present; otherwise take the earliest known start.
        const resolved =
          server ??
          local ??
          Date.now();

        writeLocal(resolved);
        if (!cancelled) setStartedAt(resolved);

        // Backfill server if missing.
        if (!server) {
          await supabase
            .from("profiles")
            .update({ sample_started_at: new Date(resolved).toISOString() })
            .eq("user_id", user.id);
        }
      } catch {
        // Network fail — fall back to local-only so UX never blocks.
        const ts = local ?? Date.now();
        writeLocal(ts);
        if (!cancelled) setStartedAt(ts);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  const value = useMemo<Ctx>(() => {
    if (startedAt == null) {
      return { startedAt: null, remainingMs: SAMPLE_MS, expired: false, suppressUpsells: true, ready };
    }
    const elapsed = Math.max(0, now - startedAt);
    const remainingMs = Math.max(0, SAMPLE_MS - elapsed);
    const expired = remainingMs <= 0;
    return {
      startedAt,
      remainingMs,
      expired,
      // Suppress upsells until the window expires.
      suppressUpsells: !expired,
      ready,
    };
  }, [startedAt, now, ready]);

  return <SampleCtx.Provider value={value}>{children}</SampleCtx.Provider>;
}

export function useSampleWindow() {
  return useContext(SampleCtx);
}

/** Format ms as "MM:SS" for chip display. */
export function formatRemaining(ms: number): string {
  const totalSec = Math.ceil(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export const SAMPLE_WINDOW_MS = SAMPLE_MS;